import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDeNumber } from '@/lib/utils';
import { statusLabel } from '@/lib/deLabels';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader,
  DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Plus, Trash2, Music, PackageCheck, Wrench, Archive,
  ArrowRightLeft, Search, CheckCircle2, Bell, Send, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../../components/layout/Layout';
import api from '../../lib/axios';
import DateInput from '@/components/ui/date-input';
import useListKeyboardNav, { LIST_ROW_CLASS } from '@/hooks/useListKeyboardNav';
import useTableSort from '@/hooks/useTableSort';
import { SortButton } from '@/components/table/SortableTh';
import EmailRecipientFields, { emptyRoutingState, routingApiPayload } from '@/components/email/EmailRecipientFields';

const emptyInstrument = {
  inventory_number: '', type: '', brand: '', model: '', size: '',
  serial_number: '', acquisition_date: '', acquisition_price: '',
  insurance_value: '', condition_notes: '', status: 'available',
  photos: [], notes: '',
};

const emptyRental = {
  instrument_id: '', student_id: '', payer_guardian_id: '', start_date: new Date().toISOString().split('T')[0],
  end_date: '', monthly_fee: '', deposit: '', insurance_included: false,
  termination_notice_days: 30, handover_condition: '',
  handover_checklist: {}, photos_handover: [], notes: '', billing_tenant_id: '',
};

const STOCK_CHIPS = [
  { code: '', label: 'Alle Instrumente' },
  { code: 'MA', label: 'MA' },
  { code: 'MAO', label: 'MAO' },
  { code: 'MAH', label: 'MAH' },
  { code: 'MAW', label: 'MAW' },
  { code: 'MAG', label: 'MAG' },
  { code: 'TA', label: 'TA' },
];

const emptyReturn = {
  return_date: new Date().toISOString().split('T')[0],
  return_condition: '', return_condition_rating: 'ok',
  photos_return: [], deposit_refund: '', repair_cost: '', return_notes: '',
};

const STATUS_LABEL = {
  available: 'Verfügbar', rented: 'Verliehen',
  maintenance: 'In Reparatur', decommissioned: 'Ausgemustert',
};
const RENTAL_STATUS_LABEL = { active: 'Aktiv', returned: 'Zurückgegeben', terminated: 'Gekündigt' };
const CONDITION_LABEL = { ok: 'Einwandfrei', scratched: 'Kratzer', damaged: 'Beschädigt' };

const INSTRUMENT_COLUMNS = [
  { key: 'inventory_number', label: 'Inv.-Nr.' },
  { key: 'type', label: 'Typ' },
  { key: 'owner_code', label: 'Eigentümer' },
  { key: 'current_place', label: 'Ort' },
  { key: 'brand', label: 'Marke / Modell' },
  { key: 'size', label: 'Größe' },
  { key: 'serial_number', label: 'Serien-Nr.' },
  { key: 'rental_tariff_fee', label: 'Mietgebühr' },
  { key: 'status', label: 'Status' },
  { key: 'active_renter_name', label: 'Vertragsnehmer' },
  { key: 'purchase_date', label: 'Anschaffung' },
];

// Status nach Klartext sortieren: „Verfügbar“ vor „Vermietet“ ist die Reihenfolge,
// die in der Tabelle steht — die englischen Codes wären eine andere.
const INSTRUMENT_SORT_ACCESSORS = {
  inventory_number: (i) => i.inventory_number,
  type: (i) => i.type,
  owner_code: (i) => i.owner_code,
  current_place: (i) => i.current_place,
  brand: (i) => [i.brand, i.model].filter(Boolean).join(' '),
  size: (i) => i.size,
  serial_number: (i) => i.serial_number,
  rental_tariff_fee: (i) => (i.rental_tariff_name ? Number(i.rental_tariff_fee || 0) : null),
  status: (i) => STATUS_LABEL[i.status] || i.status,
  active_renter_name: (i) => i.active_renter_name,
  purchase_date: (i) => i.purchase_date,
};

const RENTAL_COLUMNS = [
  { key: 'contract_number', label: 'Vertrag-Nr.' },
  { key: 'instrument_label', label: 'Instrument' },
  { key: 'owner_code', label: 'Eigentümer' },
  { key: 'billing_code', label: 'Abrechnung' },
  { key: 'payer_name', label: 'Zahlungspflichtige:r' },
  { key: 'student_name', label: 'Schüler' },
  { key: 'start_date', label: 'Start' },
  { key: 'monthly_fee', label: 'Miete / Monat' },
  { key: 'status', label: 'Status' },
];

function payerLastNameMatches(lastName, query) {
  const hay = String(lastName || '').toLocaleLowerCase('de-DE');
  const tokens = String(query || '').trim().toLocaleLowerCase('de-DE').split(/\s+/).filter(Boolean);
  return tokens.length > 0 && tokens.every((token) => hay.includes(token));
}

function surnamePrefix(query) {
  const needle = String(query || '').trim().replace(/\s+/g, ' ').toLocaleLowerCase('de-DE');
  if (!needle) return '';
  return needle.charAt(0).toLocaleUpperCase('de-DE') + needle.slice(1);
}

function payerLastNameRank(lastName, query) {
  const hay = String(lastName || '').replace(/\s+/g, ' ').trim();
  const folded = hay.toLocaleLowerCase('de-DE');
  const needle = String(query || '').trim().replace(/\s+/g, ' ').toLocaleLowerCase('de-DE');
  if (!needle) return 2;
  if (folded === needle) return 0;
  if (hay.startsWith(surnamePrefix(query))) return 1;
  return 2;
}

function payerHit(g) {
  if (g?.is_payer === false) return null;
  const last = String(g?.last_name || '').replace(/\s+/g, ' ').trim();
  const first = String(g?.first_name || '').replace(/\s+/g, ' ').trim();
  const label = [last, first].filter(Boolean).join(', ');
  if (!last || !label || label.length > 60 || /\d/.test(label)) return null;
  const email = String(g?.email || '').trim();
  const subtitle = email.includes('@') && email.length <= 80 && !/\s/.test(email) ? email : '';
  return g?.id ? { id: g.id, label, subtitle, lastName: last } : null;
}

const RENTAL_SORT_ACCESSORS = {
  contract_number: (r) => r.contract_number,
  instrument_label: (r) => r.instrument_label,
  owner_code: (r) => r.owner_code,
  billing_code: (r) => r.billing_code,
  payer_name: (r) => r.payer_name,
  student_name: (r) => r.student_name,
  start_date: (r) => r.start_date,
  monthly_fee: (r) => Number(r.monthly_fee || 0),
  status: (r) => RENTAL_STATUS_LABEL[r.status] || r.status,
};

function StockChips({ value, onChange, testId }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testId}>
      {STOCK_CHIPS.map((chip) => {
        const active = value === chip.code;
        return (
          <button
            type="button"
            key={chip.code || 'alle'}
            data-testid={`${testId}-${chip.code || 'alle'}`}
            className={`rounded-full border px-3 py-1 text-sm ${active ? 'bg-brand-anthracite text-white border-brand-anthracite' : 'bg-white text-slate-700 border-slate-200'}`}
            onClick={() => onChange(chip.code)}
          >
            {chip.label}
          </button>
        );
      })}
    </div>
  );
}

export default function InstrumentRentalPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('instruments');
  const [instruments, setInstruments] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [instrumentStats, setInstrumentStats] = useState(null);
  const [rentalStats, setRentalStats] = useState(null);
  const [rentalTariffs, setRentalTariffs] = useState([]);
  const [payerQuery, setPayerQuery] = useState('');
  const [payerHits, setPayerHits] = useState([]);
  const [payerPickedLabel, setPayerPickedLabel] = useState('');
  const [studentQuery, setStudentQuery] = useState('');
  const [studentPickedLabel, setStudentPickedLabel] = useState('');
  const [linkedStudents, setLinkedStudents] = useState([]);
  const [payerSuggestOpen, setPayerSuggestOpen] = useState(false);
  const [studentSuggestOpen, setStudentSuggestOpen] = useState(false);

  const [instrumentFilter, setInstrumentFilter] = useState(null);
  const [stockCode, setStockCode] = useState('');
  const [ownerStock, setOwnerStock] = useState('');
  const [billingStock, setBillingStock] = useState('');
  const [billingTenants, setBillingTenants] = useState([]);
  const [rentalFilter, setRentalFilter] = useState('active');
  const [search, setSearch] = useState('');
  const [rentalSearch, setRentalSearch] = useState({
    instrument: '', payer: '', party: '', contract: '',
    startFrom: '', startTo: '', endFrom: '', endTo: '',
  });
  const [focusedRentalId, setFocusedRentalId] = useState(null);
  const [focusedInstrumentId, setFocusedInstrumentId] = useState(null);

  const [instrumentForm, setInstrumentForm] = useState(emptyInstrument);
  const [showInstrumentDialog, setShowInstrumentDialog] = useState(false);
  const [editingInstrument, setEditingInstrument] = useState(null);

  const [rentalForm, setRentalForm] = useState(emptyRental);
  const [showRentalDialog, setShowRentalDialog] = useState(false);

  const [returnForm, setReturnForm] = useState(emptyReturn);
  const [returnRental, setReturnRental] = useState(null);

  // Reminders
  const [reminderPreview, setReminderPreview] = useState(null);
  const [reminderHistory, setReminderHistory] = useState([]);
  const [reminderConfig, setReminderConfig] = useState({ return_days: 30, purchase_months: 24, dedupe_days: 30 });
  const [selectedReminders, setSelectedReminders] = useState({});
  const [reminderSending, setReminderSending] = useState(false);
  const [reminderRouting, setReminderRouting] = useState(emptyRoutingState());

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadInstruments(); loadStats(); /* eslint-disable-next-line */ }, [instrumentFilter, search, stockCode]);
  useEffect(() => { loadRentals(); /* eslint-disable-next-line */ }, [rentalFilter, rentalSearch, ownerStock, billingStock]);

  const openRentalContract = (instrument) => {
    if (!instrument.active_rental_id) return;
    setRentalFilter('active');
    setFocusedRentalId(instrument.active_rental_id);
    setTab('rentals');
  };

  const openInstrumentInInventory = (rental) => {
    if (!rental?.instrument_id) return;
    setSearch('');
    setInstrumentFilter(null);
    setStockCode('');
    setFocusedInstrumentId(rental.instrument_id);
    setTab('instruments');
  };

  useEffect(() => {
    if (tab !== 'instruments' || !focusedInstrumentId) return;
    document.querySelector('[data-testid^="instrument-row-"][data-focused="true"]')
      ?.scrollIntoView({ block: 'nearest' });
  }, [tab, focusedInstrumentId, instruments]);

  const openInstrumentEdit = (i) => {
    setEditingInstrument(i);
    setInstrumentForm({ ...emptyInstrument, ...i, photos: i.photos || [] });
    setShowInstrumentDialog(true);
  };

  const instrumentSort = useTableSort(INSTRUMENT_SORT_ACCESSORS, { key: 'inventory_number' });
  const rentalSort = useTableSort(RENTAL_SORT_ACCESSORS, { key: 'start_date', dir: 'desc' });
  const sortedInstruments = instrumentSort.sortRows(instruments);
  const sortedRentals = rentalSort.sortRows(rentals);

  const { getRowProps: getInstrumentRowProps } = useListKeyboardNav({
    items: sortedInstruments,
    onSelect: openInstrumentEdit,
    enabled: !showInstrumentDialog,
  });

  const loadAll = async () => {
    await Promise.all([loadInstruments(), loadRentals(), loadStats(), loadRentalTariffs(), loadBillingTenants()]);
  };

  const loadBillingTenants = async () => {
    try {
      const { data } = await api.get('/tenants/me');
      setBillingTenants(data?.available_tenants || []);
    } catch {
      setBillingTenants([]);
    }
  };

  const sessionBillingTenant = () => (
    (typeof window !== 'undefined' && localStorage.getItem('active_tenant_id')) || ''
  );

  const startRentalFor = (instrument) => {
    resetRentalPeople();
    setRentalForm({
      ...emptyRental,
      instrument_id: instrument?.id || '',
      monthly_fee: instrument?.rental_tariff_fee != null ? String(instrument.rental_tariff_fee) : '',
      billing_tenant_id: sessionBillingTenant(),
    });
    setShowRentalDialog(true);
  };

  const loadRentalTariffs = async () => {
    try {
      const r = await api.get('/rental-tariffs');
      setRentalTariffs(r.data);
    } catch { /* tenant may not have any */ }
  };

  const loadStats = async () => {
    try {
      const [i, r] = await Promise.all([
        api.get('/instrument-rental/instruments/stats', { params: stockCode ? { stock: stockCode } : {} }),
        api.get('/instrument-rental/rentals/stats'),
      ]);
      setInstrumentStats(i.data);
      setRentalStats(r.data);
    } catch (e) { /* ignore */ }
  };

  const loadInstruments = async () => {
    try {
      const params = {};
      if (instrumentFilter) params.status = instrumentFilter;
      if (search) params.search = search;
      if (stockCode) params.stock = stockCode;
      const { data } = await api.get('/instrument-rental/instruments', { params });
      setInstruments(data);
    } catch (e) { toast.error('Instrumente laden fehlgeschlagen'); }
  };

  const loadRentals = async () => {
    try {
      const params = {};
      if (rentalFilter) params.status = rentalFilter;
      if (rentalSearch.instrument) params.q_instrument = rentalSearch.instrument;
      if (rentalSearch.payer) params.q_payer = rentalSearch.payer;
      if (rentalSearch.party) params.q_party = rentalSearch.party;
      if (rentalSearch.contract) params.q_contract = rentalSearch.contract;
      if (rentalSearch.startFrom) params.start_from = rentalSearch.startFrom;
      if (rentalSearch.startTo) params.start_to = rentalSearch.startTo;
      if (rentalSearch.endFrom) params.end_from = rentalSearch.endFrom;
      if (rentalSearch.endTo) params.end_to = rentalSearch.endTo;
      if (ownerStock) params.owner_stock = ownerStock;
      if (billingStock) params.billing_stock = billingStock;
      const { data } = await api.get('/instrument-rental/rentals', { params });
      setRentals(data);
    } catch (e) { toast.error('Mietverträge laden fehlgeschlagen'); }
  };

  const resetRentalPeople = () => {
    setPayerQuery('');
    setPayerHits([]);
    setPayerPickedLabel('');
    setStudentQuery('');
    setStudentPickedLabel('');
    setLinkedStudents([]);
    setPayerSuggestOpen(false);
    setStudentSuggestOpen(false);
  };

  useEffect(() => {
    const q = payerQuery.trim();
    if (!showRentalDialog || q.length < 2 || q === payerPickedLabel) {
      setPayerHits([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get('/guardians', {
          params: { search: q, is_payer: true, name_only: true, limit: 20 },
        });
        const rows = Array.isArray(data) ? data : (data?.items || []);
        if (cancelled) return;
        setPayerHits(rows
          .map(payerHit)
          .filter((hit) => hit && payerLastNameMatches(hit.lastName, q))
          .sort((a, b) => payerLastNameRank(a.lastName, q) - payerLastNameRank(b.lastName, q)
            || a.lastName.localeCompare(b.lastName, 'de')
            || a.label.localeCompare(b.label, 'de')));
      } catch {
        if (!cancelled) setPayerHits([]);
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [payerQuery, payerPickedLabel, showRentalDialog]);

  useEffect(() => {
    const payerId = rentalForm.payer_guardian_id;
    if (!payerId) {
      setLinkedStudents([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/guardians/${payerId}/account`);
        const rows = data?.students || [];
        if (!cancelled) {
          setLinkedStudents(rows.map((s) => ({
            id: s.id,
            label: s.name || '',
          })).filter((s) => s.id && s.label));
        }
      } catch {
        if (!cancelled) setLinkedStudents([]);
      }
    })();
    return () => { cancelled = true; };
  }, [rentalForm.payer_guardian_id]);

  // --- Instrument CRUD ---
  const saveInstrument = async () => {
    try {
      const payload = { ...instrumentForm };
      ['acquisition_price', 'insurance_value'].forEach(k => {
        if (payload[k] === '') payload[k] = null;
      });
      if (payload.acquisition_date === '') payload.acquisition_date = null;

      if (editingInstrument) {
        await api.put(`/instrument-rental/instruments/${editingInstrument.id}`, payload);
        toast.success('Instrument aktualisiert');
      } else {
        await api.post('/instrument-rental/instruments', payload);
        toast.success('Instrument angelegt');
      }
      setShowInstrumentDialog(false);
      setEditingInstrument(null);
      setInstrumentForm(emptyInstrument);
      loadInstruments(); loadStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    }
  };

  const deleteInstrument = async (id) => {
    if (!window.confirm('Instrument löschen?')) return;
    try {
      await api.delete(`/instrument-rental/instruments/${id}`);
      toast.success('Gelöscht');
      loadInstruments(); loadStats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen'); }
  };

  // --- Rental CRUD ---
  const saveRental = async () => {
    try {
      const payload = { ...rentalForm };
      ['monthly_fee', 'deposit'].forEach(k => {
        if (payload[k] === '') payload[k] = 0;
      });
      ['student_id', 'payer_guardian_id', 'billing_tenant_id', 'end_date'].forEach(k => {
        if (payload[k] === '') payload[k] = null;
      });
      await api.post('/instrument-rental/rentals', payload);
      toast.success('Mietvertrag angelegt');
      setShowRentalDialog(false);
      setRentalForm(emptyRental);
      resetRentalPeople();
      loadRentals(); loadInstruments(); loadStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Anlegen fehlgeschlagen');
    }
  };

  const doReturn = async () => {
    try {
      const payload = { ...returnForm };
      ['deposit_refund', 'repair_cost'].forEach(k => {
        if (payload[k] === '') payload[k] = null;
      });
      await api.post(`/instrument-rental/rentals/${returnRental.id}/return`, payload);
      toast.success('Instrument zurückgegeben');
      setReturnRental(null);
      setReturnForm(emptyReturn);
      loadRentals(); loadInstruments(); loadStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rückgabe fehlgeschlagen');
    }
  };

  const deleteRental = async (id) => {
    if (!window.confirm('Mietvertrag löschen?')) return;
    try {
      await api.delete(`/instrument-rental/rentals/${id}`);
      toast.success('Gelöscht');
      loadRentals(); loadInstruments(); loadStats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen'); }
  };

  // --- Reminders ---
  const loadReminderPreview = async () => {
    try {
      const { data } = await api.get('/instrument-rental/reminders/preview', {
        params: reminderConfig,
      });
      setReminderPreview(data);
      const initial = {};
      (data.items || []).forEach(it => {
        if (it.ready_to_send) initial[`${it.rental_id}-${it.reminder_type}`] = true;
      });
      setSelectedReminders(initial);
    } catch (e) { toast.error('Vorschau fehlgeschlagen'); }
  };

  const loadReminderHistory = async () => {
    try {
      const { data } = await api.get('/instrument-rental/reminders/history');
      setReminderHistory(data);
    } catch (e) { /* ignore */ }
  };

  const sendReminders = async () => {
    const rental_ids = Object.keys(selectedReminders).filter(k => selectedReminders[k]).map(k => k.split('-')[0]);
    const reminder_types = Array.from(new Set(
      Object.keys(selectedReminders).filter(k => selectedReminders[k]).map(k => k.split('-').slice(1).join('-'))
    ));
    if (rental_ids.length === 0) {
      toast.error('Bitte mindestens einen Reminder auswählen');
      return;
    }
    setReminderSending(true);
    try {
      const { data } = await api.post('/instrument-rental/reminders/send', {
        rental_ids,
        reminder_types,
        ...reminderConfig,
        ...routingApiPayload(reminderRouting),
      });
      toast.success(`${data.sent} gesendet, ${data.failed} fehlgeschlagen, ${data.skipped} übersprungen`);
      loadReminderPreview(); loadReminderHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Versand fehlgeschlagen');
    } finally {
      setReminderSending(false);
    }
  };

  // --- Rendering ---
  const StatCard = ({ label, value, icon: Icon, color, active, onClick, testid }) => (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`text-left rounded-xl border p-4 transition shadow-sm hover:shadow ${
        active ? 'border-slate-900 bg-slate-900 text-white' : 'bg-white border-slate-200 hover:border-slate-400'
      }`}
    >
      <div className="flex items-center gap-2 text-xs font-medium opacity-75">
        {Icon && <Icon size={14} />} {label}
      </div>
      <div className="mt-2 text-2xl font-bold">{value}</div>
    </button>
  );

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="instrument-rental-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Instrumentenvermietung</h1>
            <p className="text-sm text-slate-500 mt-1">Inventar, Leihverträge, Rückgabe & HM.-Abrechnung</p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="instrument-rental-tabs">
            <TabsTrigger value="instruments" data-testid="tab-instruments">
              <Music size={14} className="mr-2" /> Inventar
            </TabsTrigger>
            <TabsTrigger value="rentals" data-testid="tab-rentals">
              <ArrowRightLeft size={14} className="mr-2" /> Mietverträge
            </TabsTrigger>
            <TabsTrigger value="reminders" data-testid="tab-reminders"
              onClick={() => { loadReminderPreview(); loadReminderHistory(); }}>
              <Bell size={14} className="mr-2" /> Erinnerungen
            </TabsTrigger>
          </TabsList>

          {/* ---------- INSTRUMENTS ---------- */}
          <TabsContent value="instruments" className="mt-4 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard testid="stat-available" label="Verfügbar"
                value={instrumentStats?.available ?? 0} icon={PackageCheck}
                active={instrumentFilter === 'available'}
                onClick={() => setInstrumentFilter(instrumentFilter === 'available' ? null : 'available')} />
              <StatCard testid="stat-rented" label="Verliehen"
                value={instrumentStats?.rented ?? 0} icon={ArrowRightLeft}
                active={instrumentFilter === 'rented'}
                onClick={() => setInstrumentFilter(instrumentFilter === 'rented' ? null : 'rented')} />
              <StatCard testid="stat-maintenance" label="In Reparatur"
                value={instrumentStats?.maintenance ?? 0} icon={Wrench}
                active={instrumentFilter === 'maintenance'}
                onClick={() => setInstrumentFilter(instrumentFilter === 'maintenance' ? null : 'maintenance')} />
              <StatCard testid="stat-decommissioned" label="Ausgemustert"
                value={instrumentStats?.decommissioned ?? 0} icon={Archive}
                active={instrumentFilter === 'decommissioned'}
                onClick={() => setInstrumentFilter(instrumentFilter === 'decommissioned' ? null : 'decommissioned')} />
            </div>

            <Card>
              <CardContent className="pt-6">
                <div className="mb-3">
                  <StockChips value={stockCode} onChange={setStockCode} testId="instrument-stock" />
                </div>
                <div className="flex items-center justify-between gap-3 mb-4">
                  <div className="relative flex-1">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input data-testid="instrument-search" placeholder="Suche (Inv.-Nr., Tarif, Vertragsnehmer, Größe, Marke, Typ)"
                      value={search} onChange={(e) => setSearch(e.target.value)}
                      className="pl-9" />
                  </div>
                  <Button data-testid="new-instrument-btn" onClick={() => {
                    setEditingInstrument(null); setInstrumentForm(emptyInstrument); setShowInstrumentDialog(true);
                  }}>
                    <Plus size={16} className="mr-1" /> Neues Instrument
                  </Button>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow>
                      {INSTRUMENT_COLUMNS.map((col) => (
                        <TableHead key={col.key}>
                          <SortButton
                            sortKey={col.key}
                            sort={instrumentSort.sort}
                            onSort={instrumentSort.toggleSort}
                            testIdPrefix="instruments-sort"
                          >
                            {col.label}
                          </SortButton>
                        </TableHead>
                      ))}
                      <TableHead className="w-12 text-right" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {instruments.length === 0 && (
                      <TableRow><TableCell colSpan={12} className="text-center text-slate-400 py-10">
                        Noch keine Instrumente angelegt.
                      </TableCell></TableRow>
                    )}
                    {sortedInstruments.map((i, idx) => (
                      <TableRow
                        key={i.id}
                        data-testid={`instrument-row-${i.inventory_number}`}
                        className={`${LIST_ROW_CLASS}${focusedInstrumentId === i.id ? ' bg-brand-red/10' : ''}`}
                        {...getInstrumentRowProps(idx, i)}
                        data-focused={focusedInstrumentId === i.id ? 'true' : undefined}
                      >
                        <TableCell className="font-mono">{i.inventory_number}</TableCell>
                        <TableCell className="font-medium">{i.type}</TableCell>
                        <TableCell data-testid={`instrument-owner-${i.inventory_number}`}>{i.owner_code || '—'}</TableCell>
                        <TableCell data-testid={`instrument-place-${i.inventory_number}`}>{i.current_place || '—'}</TableCell>
                        <TableCell>{[i.brand, i.model].filter(Boolean).join(' ') || '—'}</TableCell>
                        <TableCell>{i.size || '—'}</TableCell>
                        <TableCell className="font-mono text-xs">{i.serial_number || '—'}</TableCell>
                        <TableCell className="text-xs">
                          {i.rental_tariff_name ? (
                            <div>
                              <div className="font-semibold text-brand-red">{i.rental_tariff_name}</div>
                              <div className="text-slate-500 font-mono">{formatDeNumber(i.rental_tariff_fee || 0)} €/Mo</div>
                            </div>
                          ) : (
                            <span className="text-slate-300 italic">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            i.status === 'available' ? 'bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100' :
                            i.status === 'rented' ? 'bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-100' :
                            'bg-red-100 text-red-700 border-red-200 hover:bg-red-100'
                          } data-testid={`instrument-status-${i.id}`}>
                            {STATUS_LABEL[i.status] || statusLabel(i.status)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {i.active_renter_name && i.active_rental_id ? (
                            <button
                              type="button"
                              className="text-left text-brand-red font-medium hover:underline"
                              data-testid={`instrument-renter-${i.inventory_number}`}
                              onClick={() => openRentalContract(i)}
                            >
                              {i.active_renter_name}
                            </button>
                          ) : '—'}
                        </TableCell>
                        <TableCell className="text-sm">
                          {i.acquisition_date || '—'}
                          {i.acquisition_price && <div className="text-xs text-slate-500">{formatDeNumber(i.acquisition_price)} €</div>}
                        </TableCell>
                        <TableCell className="text-right">
                          {i.status === 'available' && (
                            <Button size="sm" variant="outline" data-testid={`instrument-rent-${i.inventory_number}`}
                              onClick={(e) => { e.stopPropagation(); startRentalFor(i); }}>
                              Mietvertrag erstellen
                            </Button>
                          )}
                          <Button size="icon" variant="ghost" data-testid={`delete-instrument-${i.inventory_number}`}
                            onClick={() => deleteInstrument(i.id)} title="Löschen">
                            <Trash2 size={14} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ---------- RENTALS ---------- */}
          <TabsContent value="rentals" className="mt-4 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard testid="stat-rental-total" label="Gesamt" value={rentalStats?.total ?? 0} icon={ArrowRightLeft}
                active={!rentalFilter} onClick={() => setRentalFilter(null)} />
              <StatCard testid="stat-rental-active" label="Aktiv" value={rentalStats?.active?.count ?? 0} icon={CheckCircle2}
                active={rentalFilter === 'active'} onClick={() => setRentalFilter(rentalFilter === 'active' ? null : 'active')} />
              <StatCard testid="stat-rental-returned" label="Zurück" value={rentalStats?.returned?.count ?? 0} icon={PackageCheck}
                active={rentalFilter === 'returned'} onClick={() => setRentalFilter(rentalFilter === 'returned' ? null : 'returned')} />
              <StatCard testid="stat-rental-sum" label="Monatsumsatz"
                value={`${formatDeNumber(rentalStats?.active?.monthly_sum ?? 0)} €`}
                icon={ArrowRightLeft}
                active={false} onClick={() => {}} />
            </div>

            <Card>
              <CardContent className="pt-6">
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-slate-500 w-24">Eigentümer</span>
                    <StockChips value={ownerStock} onChange={setOwnerStock} testId="rental-owner-stock" />
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-slate-500 w-24">Abrechnung</span>
                    <StockChips value={billingStock} onChange={setBillingStock} testId="rental-billing-stock" />
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4" data-testid="rental-search">
                  <Input data-testid="rental-search-instrument" placeholder="Instrument / Inv.-Nr."
                    value={rentalSearch.instrument}
                    onChange={(e) => setRentalSearch((s) => ({ ...s, instrument: e.target.value }))} />
                  <Input data-testid="rental-search-payer" placeholder="Zahlungspflichtige:r"
                    value={rentalSearch.payer}
                    onChange={(e) => setRentalSearch((s) => ({ ...s, payer: e.target.value }))} />
                  <Input data-testid="rental-search-party" placeholder="Vertragsnehmer"
                    value={rentalSearch.party}
                    onChange={(e) => setRentalSearch((s) => ({ ...s, party: e.target.value }))} />
                  <Input data-testid="rental-search-contract" placeholder="Vertragsnummer"
                    value={rentalSearch.contract}
                    onChange={(e) => setRentalSearch((s) => ({ ...s, contract: e.target.value }))} />
                  <div data-testid="rental-search-start-from">
                    <DateInput placeholder="Beginn von" value={rentalSearch.startFrom}
                      onChange={(e) => setRentalSearch((s) => ({ ...s, startFrom: e.target.value }))} />
                  </div>
                  <div data-testid="rental-search-start-to">
                    <DateInput placeholder="Beginn bis" value={rentalSearch.startTo}
                      onChange={(e) => setRentalSearch((s) => ({ ...s, startTo: e.target.value }))} />
                  </div>
                  <div data-testid="rental-search-end-from">
                    <DateInput placeholder="Ende von" value={rentalSearch.endFrom}
                      onChange={(e) => setRentalSearch((s) => ({ ...s, endFrom: e.target.value }))} />
                  </div>
                  <div data-testid="rental-search-end-to">
                    <DateInput placeholder="Ende bis" value={rentalSearch.endTo}
                      onChange={(e) => setRentalSearch((s) => ({ ...s, endTo: e.target.value }))} />
                  </div>
                </div>
                <div className="flex items-center justify-between mb-4">
                  <div className="text-sm text-slate-500">
                    {rentals.length} Vertrag{rentals.length !== 1 && 'e'}
                    {rentalFilter && ` • Filter: ${RENTAL_STATUS_LABEL[rentalFilter]}`}
                  </div>
                  <Button data-testid="new-rental-btn"
                    onClick={() => startRentalFor(null)}
                    disabled={instruments.filter(i => i.status === 'available').length === 0}>
                    <Plus size={16} className="mr-1" /> Neuer Mietvertrag
                  </Button>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow>
                      {RENTAL_COLUMNS.map((col) => (
                        <TableHead key={col.key}>
                          <SortButton
                            sortKey={col.key}
                            sort={rentalSort.sort}
                            onSort={rentalSort.toggleSort}
                            testIdPrefix="rentals-sort"
                          >
                            {col.label}
                          </SortButton>
                        </TableHead>
                      ))}
                      <TableHead className="w-32 text-right">Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rentals.length === 0 && (
                      <TableRow><TableCell colSpan={10} className="text-center text-slate-400 py-10">
                        Keine Mietverträge vorhanden.
                      </TableCell></TableRow>
                    )}
                    {sortedRentals.map(r => (
                      <TableRow
                        key={r.id}
                        data-testid={`rental-row-${r.contract_number}`}
                        data-focused={focusedRentalId === r.id ? 'true' : undefined}
                        className={focusedRentalId === r.id ? 'bg-brand-red/10' : undefined}
                      >
                        <TableCell className="font-mono text-xs">{r.contract_number}</TableCell>
                        <TableCell>
                          {r.instrument_id && r.instrument_label ? (
                            <button
                              type="button"
                              className="text-left text-brand-red font-medium hover:underline"
                              data-testid={`rental-instrument-${r.contract_number}`}
                              onClick={() => openInstrumentInInventory(r)}
                            >
                              {r.instrument_label}
                            </button>
                          ) : (r.instrument_label || '—')}
                        </TableCell>
                        <TableCell data-testid={`rental-owner-${r.contract_number}`}>{r.owner_code || '—'}</TableCell>
                        <TableCell data-testid={`rental-billing-${r.contract_number}`}>{r.billing_code || '—'}</TableCell>
                        <TableCell>
                          {r.payer_guardian_id && r.payer_name ? (
                            <button
                              type="button"
                              className="text-left text-brand-red font-medium hover:underline"
                              data-testid={`rental-payer-${r.contract_number}`}
                              onClick={() => navigate(`/payer-accounts?guardian=${r.payer_guardian_id}`)}
                            >
                              {r.payer_name}
                            </button>
                          ) : '—'}
                        </TableCell>
                        <TableCell>{r.student_name || '—'}</TableCell>
                        <TableCell className="text-sm">{r.start_date}</TableCell>
                        <TableCell className="font-mono">{formatDeNumber(r.monthly_fee)} €</TableCell>
                        <TableCell>
                          <Badge variant={r.status === 'active' ? 'default' : 'outline'}>
                            {RENTAL_STATUS_LABEL[r.status] || statusLabel(r.status)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {r.status === 'active' && (
                            <Button size="sm" variant="outline" data-testid={`return-rental-${r.contract_number}`}
                              onClick={() => { setReturnRental(r); setReturnForm(emptyReturn); }}>
                              Rückgabe
                            </Button>
                          )}
                          <Button size="icon" variant="ghost" data-testid={`delete-rental-${r.contract_number}`}
                            onClick={() => deleteRental(r.id)}>
                            <Trash2 size={14} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ---------- REMINDERS ---------- */}
          <TabsContent value="reminders" className="mt-4 space-y-4">
            <Card>
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-center gap-3">
                  <div>
                    <Label className="text-xs">Rückgabe X Tage vor Ende</Label>
                    <Input data-testid="reminder-return-days" type="number" className="w-24"
                      value={reminderConfig.return_days}
                      onChange={e => setReminderConfig({ ...reminderConfig, return_days: parseInt(e.target.value || '0', 10) })} />
                  </div>
                  <div>
                    <Label className="text-xs">Kaufoption nach X Monaten</Label>
                    <Input data-testid="reminder-purchase-months" type="number" className="w-24"
                      value={reminderConfig.purchase_months}
                      onChange={e => setReminderConfig({ ...reminderConfig, purchase_months: parseInt(e.target.value || '0', 10) })} />
                  </div>
                  <div>
                    <Label className="text-xs">Dedupe (Tage)</Label>
                    <Input data-testid="reminder-dedupe-days" type="number" className="w-24"
                      value={reminderConfig.dedupe_days}
                      onChange={e => setReminderConfig({ ...reminderConfig, dedupe_days: parseInt(e.target.value || '0', 10) })} />
                  </div>
                  <div className="flex-1" />
                  <Button data-testid="reminder-refresh-btn" variant="outline" onClick={loadReminderPreview}>
                    <Search size={14} className="mr-1" /> Vorschau aktualisieren
                  </Button>
                  <Button data-testid="reminder-send-btn" onClick={sendReminders}
                    disabled={reminderSending || !reminderPreview || reminderPreview.ready_to_send === 0}>
                    <Send size={14} className="mr-1" /> Auswahl senden
                  </Button>
                </div>
                <EmailRecipientFields
                  extraTo={reminderRouting.extraTo}
                  cc={reminderRouting.cc}
                  bcc={reminderRouting.bcc}
                  onChange={setReminderRouting}
                  disabled={reminderSending}
                  testId="rental-reminder-routing"
                />

                {reminderPreview && (
                  <div className="grid grid-cols-3 gap-3">
                    <StatCard testid="reminder-stat-total" label="Gesamt fällig" value={reminderPreview.total} icon={Bell} active={false} onClick={() => {}} />
                    <StatCard testid="reminder-stat-ready" label="Versandbereit" value={reminderPreview.ready_to_send} icon={CheckCircle2} active={false} onClick={() => {}} />
                    <StatCard testid="reminder-stat-blocked" label="Blockiert"
                      value={(reminderPreview.total - reminderPreview.ready_to_send)}
                      icon={AlertCircle} active={false} onClick={() => {}} />
                  </div>
                )}

                {reminderPreview && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10"></TableHead>
                        <TableHead>Vertrag</TableHead>
                        <TableHead>Typ</TableHead>
                        <TableHead>Instrument</TableHead>
                        <TableHead>Trigger</TableHead>
                        <TableHead>Empfänger</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reminderPreview.items.length === 0 && (
                        <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10">
                          Aktuell keine fälligen Erinnerungen.
                        </TableCell></TableRow>
                      )}
                      {reminderPreview.items.map(it => {
                        const key = `${it.rental_id}-${it.reminder_type}`;
                        return (
                          <TableRow key={key} data-testid={`reminder-row-${it.contract_number}-${it.reminder_type}`}>
                            <TableCell>
                              <input type="checkbox" data-testid={`reminder-select-${it.contract_number}-${it.reminder_type}`}
                                disabled={!it.ready_to_send}
                                checked={!!selectedReminders[key]}
                                onChange={e => setSelectedReminders({ ...selectedReminders, [key]: e.target.checked })} />
                            </TableCell>
                            <TableCell className="font-mono text-xs">{it.contract_number}</TableCell>
                            <TableCell>
                              <Badge variant={it.reminder_type === 'return_due' ? 'default' : 'secondary'}>
                                {it.reminder_label}
                              </Badge>
                            </TableCell>
                            <TableCell>{it.instrument_label}</TableCell>
                            <TableCell className="text-sm">
                              {it.reminder_type === 'return_due' && `Ende ${it.trigger_at} (${it.days_left} Tage)`}
                              {it.reminder_type === 'purchase_option' && `${it.months_rented} Monate Mietdauer`}
                            </TableCell>
                            <TableCell className="text-sm">
                              {it.recipient_email ? `${it.recipient_name} <${it.recipient_email}>` : '—'}
                            </TableCell>
                            <TableCell>
                              {it.ready_to_send && <Badge>Bereit</Badge>}
                              {it.recipient_missing && <Badge variant="destructive">Keine E-Mail</Badge>}
                              {it.already_sent_recent && <Badge variant="outline">Bereits gesendet</Badge>}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}

                <div>
                  <h3 className="text-sm font-semibold mb-2 mt-6">Versand-Historie (letzte 200)</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Gesendet am</TableHead>
                        <TableHead>Vertrag</TableHead>
                        <TableHead>Typ</TableHead>
                        <TableHead>Empfänger</TableHead>
                        <TableHead>Betreff</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reminderHistory.length === 0 && (
                        <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-6">
                          Noch keine Erinnerungen versendet.
                        </TableCell></TableRow>
                      )}
                      {reminderHistory.map(rr => (
                        <TableRow key={rr.id} data-testid={`reminder-hist-${rr.id}`}>
                          <TableCell className="text-sm">{rr.sent_at?.replace('T', ' ').slice(0, 16)}</TableCell>
                          <TableCell className="font-mono text-xs">{rr.rental_id?.slice(0, 8)}…</TableCell>
                          <TableCell>{rr.reminder_label}</TableCell>
                          <TableCell className="text-sm">{rr.recipient_email}</TableCell>
                          <TableCell className="text-sm">{rr.subject}</TableCell>
                          <TableCell>
                            <Badge variant={rr.status === 'sent' ? 'default' : 'destructive'}>{statusLabel(rr.status)}</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* ---------- Instrument Dialog ---------- */}
        <Dialog open={showInstrumentDialog} onOpenChange={setShowInstrumentDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="instrument-dialog">
            <DialogHeader>
              <DialogTitle>{editingInstrument ? 'Instrument bearbeiten' : 'Neues Instrument'}</DialogTitle>
              <DialogDescription>Inventar-Datensatz für die Instrumentenvermietung.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Inventar-Nr. *</Label>
                <Input data-testid="inv-inventory_number" value={instrumentForm.inventory_number}
                  onChange={e => setInstrumentForm({ ...instrumentForm, inventory_number: e.target.value })} />
              </div>
              <div>
                <Label>Typ *</Label>
                <Input data-testid="inv-type" value={instrumentForm.type} placeholder="z. B. Violine, Cello, Flöte"
                  onChange={e => setInstrumentForm({ ...instrumentForm, type: e.target.value })} />
              </div>
              <div>
                <Label>Marke</Label>
                <Input data-testid="inv-brand" value={instrumentForm.brand}
                  onChange={e => setInstrumentForm({ ...instrumentForm, brand: e.target.value })} />
              </div>
              <div>
                <Label>Modell</Label>
                <Input data-testid="inv-model" value={instrumentForm.model}
                  onChange={e => setInstrumentForm({ ...instrumentForm, model: e.target.value })} />
              </div>
              <div>
                <Label>Größe</Label>
                <Input data-testid="inv-size" placeholder="4/4, 3/4, 1/2 …" value={instrumentForm.size}
                  onChange={e => setInstrumentForm({ ...instrumentForm, size: e.target.value })} />
              </div>
              <div>
                <Label>Seriennr.</Label>
                <Input data-testid="inv-serial_number" value={instrumentForm.serial_number}
                  onChange={e => setInstrumentForm({ ...instrumentForm, serial_number: e.target.value })} />
              </div>
              <div>
                <Label>Anschaffungsdatum</Label>
                <DateInput data-testid="inv-acquisition_date" value={instrumentForm.acquisition_date || ''}
                  onChange={e => setInstrumentForm({ ...instrumentForm, acquisition_date: e.target.value })} />
              </div>
              <div>
                <Label>Anschaffungspreis €</Label>
                <Input data-testid="inv-acquisition_price" type="number" step="0.01"
                  value={instrumentForm.acquisition_price || ''}
                  onChange={e => setInstrumentForm({ ...instrumentForm, acquisition_price: e.target.value })} />
              </div>
              <div>
                <Label>Versicherungssumme €</Label>
                <Input data-testid="inv-insurance_value" type="number" step="0.01"
                  value={instrumentForm.insurance_value || ''}
                  onChange={e => setInstrumentForm({ ...instrumentForm, insurance_value: e.target.value })} />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={instrumentForm.status}
                  onValueChange={v => setInstrumentForm({ ...instrumentForm, status: v })}>
                  <SelectTrigger data-testid="inv-status" className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_LABEL).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Mietgebühren-Tarif</Label>
                <Select
                  value={instrumentForm.rental_tariff_id || '_none'}
                  onValueChange={v => setInstrumentForm({ ...instrumentForm, rental_tariff_id: v === '_none' ? null : v })}
                >
                  <SelectTrigger data-testid="inv-rental_tariff" className="bg-white"><SelectValue placeholder="Kein Tarif" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">— kein Tarif zugewiesen —</SelectItem>
                    {rentalTariffs.map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name} · {formatDeNumber(t.monthly_fee || 0)} €/Mo
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">Beim Anlegen eines Mietvertrags wird die monatliche Gebühr aus diesem Tarif übernommen.</p>
              </div>
              <div className="col-span-2">
                <Label>Zustand / Notizen</Label>
                <Textarea data-testid="inv-condition_notes" value={instrumentForm.condition_notes || ''}
                  onChange={e => setInstrumentForm({ ...instrumentForm, condition_notes: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowInstrumentDialog(false)}>Abbrechen</Button>
              <Button data-testid="save-instrument-btn" onClick={saveInstrument}
                disabled={!instrumentForm.inventory_number || !instrumentForm.type}>
                Speichern
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ---------- Rental Dialog ---------- */}
        <Dialog open={showRentalDialog} onOpenChange={setShowRentalDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="rental-dialog">
            <DialogHeader>
              <DialogTitle>Neuer Mietvertrag (HM.)</DialogTitle>
              <DialogDescription>
                Leihinstrument vergeben. Die monatliche Miete gehört zum Konto der zahlungspflichtigen Person.
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Instrument *</Label>
                <Select value={rentalForm.instrument_id}
                  onValueChange={v => {
                    const inst = instruments.find(x => x.id === v);
                    const fee = inst?.rental_tariff_fee != null ? String(inst.rental_tariff_fee) : rentalForm.monthly_fee;
                    setRentalForm({ ...rentalForm, instrument_id: v, monthly_fee: fee });
                  }}>
                  <SelectTrigger data-testid="rental-instrument" className="bg-white"><SelectValue placeholder="Auswählen …" /></SelectTrigger>
                  <SelectContent>
                    {instruments.filter(i => i.status === 'available').map(i => (
                      <SelectItem key={i.id} value={i.id}>
                        {i.inventory_number} — {i.type} {i.size || ''} {i.brand || ''}
                        {i.rental_tariff_name ? ` · ${i.rental_tariff_name} (${formatDeNumber(i.rental_tariff_fee || 0)} €)` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Zahlungspflichtige:r (Mieter)</Label>
                <Input data-testid="rental-payer" className="bg-white" placeholder="Nachnamen eintippen …"
                  value={payerQuery}
                  onChange={(e) => {
                    const value = e.target.value;
                    setPayerQuery(value);
                    setPayerSuggestOpen(true);
                    if (value.trim() !== payerPickedLabel) {
                      setPayerPickedLabel('');
                      setRentalForm((form) => ({ ...form, payer_guardian_id: '', student_id: '' }));
                      setLinkedStudents([]);
                      setStudentQuery('');
                      setStudentPickedLabel('');
                    }
                  }}
                  onFocus={() => setPayerSuggestOpen(true)}
                  onBlur={() => setPayerSuggestOpen(false)} />
                {payerSuggestOpen && payerHits.length > 0 && (
                  <ul className="mt-1 border border-slate-200 rounded-md bg-white max-h-40 overflow-y-auto" data-testid="rental-payer-hits">
                    {payerHits.map((hit) => (
                      <li key={hit.id}>
                        <button type="button" data-testid={`rental-payer-hit-${hit.id}`}
                          className="w-full text-left px-2.5 py-1.5 border-b border-slate-100 last:border-b-0 hover:bg-slate-50"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setPayerQuery(hit.label);
                            setPayerPickedLabel(hit.label);
                            setPayerHits([]);
                            setPayerSuggestOpen(false);
                            setStudentQuery('');
                            setStudentPickedLabel('');
                            setRentalForm((form) => ({ ...form, payer_guardian_id: hit.id, student_id: '' }));
                          }}>
                          <div className="text-sm text-slate-800 truncate">{hit.label}</div>
                          {hit.subtitle ? <div className="text-xs text-slate-500 truncate">{hit.subtitle}</div> : null}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <Label>Schüler</Label>
                <Input data-testid="rental-student" className="bg-white"
                  placeholder={rentalForm.payer_guardian_id ? 'Name eintippen …' : 'Zuerst den Mieter wählen'}
                  disabled={!rentalForm.payer_guardian_id}
                  value={studentQuery}
                  onChange={(e) => {
                    const value = e.target.value;
                    setStudentQuery(value);
                    setStudentSuggestOpen(true);
                    if (value.trim() !== studentPickedLabel) {
                      setStudentPickedLabel('');
                      setRentalForm((form) => ({ ...form, student_id: '' }));
                    }
                  }}
                  onFocus={() => setStudentSuggestOpen(true)}
                  onBlur={() => setStudentSuggestOpen(false)} />
                {studentSuggestOpen && rentalForm.payer_guardian_id && (
                  <ul className="mt-1 border border-slate-200 rounded-md bg-white max-h-40 overflow-y-auto" data-testid="rental-student-hits">
                    {linkedStudents
                      .filter((hit) => {
                        const needle = studentQuery.trim().toLowerCase();
                        if (!needle || needle === studentPickedLabel.trim().toLowerCase()) return true;
                        return hit.label.toLowerCase().includes(needle);
                      })
                      .map((hit) => (
                        <li key={hit.id}>
                          <button type="button" data-testid={`rental-student-hit-${hit.id}`}
                            className="w-full text-left px-2.5 py-1.5 text-sm text-slate-800 hover:bg-slate-50"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setStudentQuery(hit.label);
                              setStudentPickedLabel(hit.label);
                              setStudentSuggestOpen(false);
                              setRentalForm((form) => ({ ...form, student_id: hit.id }));
                            }}>
                            {hit.label}
                          </button>
                        </li>
                      ))}
                  </ul>
                )}
              </div>
              <div>
                <Label>Abrechnende Niederlassung</Label>
                <Select value={rentalForm.billing_tenant_id || undefined}
                  onValueChange={v => setRentalForm({ ...rentalForm, billing_tenant_id: v })}>
                  <SelectTrigger data-testid="rental-billing-tenant" className="bg-white"><SelectValue placeholder="Niederlassung wählen" /></SelectTrigger>
                  <SelectContent>
                    {billingTenants.map((tenant) => (
                      <SelectItem key={tenant.id} value={tenant.id}>{tenant.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Startdatum *</Label>
                <DateInput data-testid="rental-start_date" value={rentalForm.start_date}
                  onChange={e => setRentalForm({ ...rentalForm, start_date: e.target.value })} />
              </div>
              <div>
                <Label>Ende (optional)</Label>
                <DateInput data-testid="rental-end_date" value={rentalForm.end_date}
                  onChange={e => setRentalForm({ ...rentalForm, end_date: e.target.value })} />
              </div>
              <div>
                <Label>Monatsmiete € *</Label>
                <Input data-testid="rental-monthly_fee" type="number" step="0.01"
                  value={rentalForm.monthly_fee} onChange={e => setRentalForm({ ...rentalForm, monthly_fee: e.target.value })} />
              </div>
              <div>
                <Label>Kaution €</Label>
                <Input data-testid="rental-deposit" type="number" step="0.01"
                  value={rentalForm.deposit} onChange={e => setRentalForm({ ...rentalForm, deposit: e.target.value })} />
              </div>
              <div>
                <Label>Kündigungsfrist (Tage)</Label>
                <Input data-testid="rental-termination_notice_days" type="number" value={rentalForm.termination_notice_days}
                  onChange={e => setRentalForm({ ...rentalForm, termination_notice_days: parseInt(e.target.value || '0', 10) })} />
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <input data-testid="rental-insurance_included" type="checkbox" checked={rentalForm.insurance_included}
                  onChange={e => setRentalForm({ ...rentalForm, insurance_included: e.target.checked })} />
                <Label>Versicherung enthalten</Label>
              </div>
              <div className="col-span-2">
                <Label>Ausgabe-Zustand / Checkliste (Text)</Label>
                <Textarea data-testid="rental-handover_condition" rows={3} value={rentalForm.handover_condition}
                  onChange={e => setRentalForm({ ...rentalForm, handover_condition: e.target.value })}
                  placeholder="z. B. Korpus einwandfrei, Bogen enthalten, Koffer neuwertig …" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRentalDialog(false)}>Abbrechen</Button>
              <Button data-testid="save-rental-btn" onClick={saveRental}
                disabled={!rentalForm.instrument_id || !rentalForm.start_date || !rentalForm.monthly_fee}>
                Anlegen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ---------- Return Dialog ---------- */}
        <Dialog open={!!returnRental} onOpenChange={(o) => !o && setReturnRental(null)}>
          <DialogContent className="max-w-xl" data-testid="return-dialog">
            <DialogHeader>
              <DialogTitle>Rückgabe – {returnRental?.contract_number}</DialogTitle>
              <DialogDescription>{returnRental?.instrument_label}</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Rückgabedatum *</Label>
                <DateInput data-testid="return-date" value={returnForm.return_date}
                  onChange={e => setReturnForm({ ...returnForm, return_date: e.target.value })} />
              </div>
              <div>
                <Label>Zustand *</Label>
                <Select value={returnForm.return_condition_rating}
                  onValueChange={v => setReturnForm({ ...returnForm, return_condition_rating: v })}>
                  <SelectTrigger data-testid="return-rating" className="bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(CONDITION_LABEL).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Kaution-Rückgabe €</Label>
                <Input data-testid="return-deposit_refund" type="number" step="0.01" value={returnForm.deposit_refund}
                  onChange={e => setReturnForm({ ...returnForm, deposit_refund: e.target.value })} />
              </div>
              <div>
                <Label>Reparaturkosten €</Label>
                <Input data-testid="return-repair_cost" type="number" step="0.01" value={returnForm.repair_cost}
                  onChange={e => setReturnForm({ ...returnForm, repair_cost: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Zustandsprotokoll</Label>
                <Textarea data-testid="return-condition_notes" rows={3} value={returnForm.return_condition}
                  onChange={e => setReturnForm({ ...returnForm, return_condition: e.target.value })}
                  placeholder="Kratzer, Dellen, Bogen vollständig, …" />
              </div>
              <div className="col-span-2">
                <Label>Interne Notizen</Label>
                <Textarea data-testid="return-notes" rows={2} value={returnForm.return_notes}
                  onChange={e => setReturnForm({ ...returnForm, return_notes: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReturnRental(null)}>Abbrechen</Button>
              <Button data-testid="confirm-return-btn" onClick={doReturn}>Rückgabe bestätigen</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
